import { formatEventTime, type EventTimeFields } from '@/lib/calendar/format-event-time';
import type { SurfaceCapabilities } from './surface-capabilities';
import { formatRelativeDate } from './format-relative-date';

/** How many parts the line may carry. Enough to identify the meeting and who
 *  was in it; past that the line competes with the title above it and is the
 *  first thing to truncate on a narrow window. */
const MAX_PARTS = 3;

export interface PropertySummaryInput {
  event: EventTimeFields | null;
  attendeeCount: number;
  /** Null while a recording is still capturing — its length is not yet a fact. */
  durationSeconds: number | null;
  tagCount: number;
  collectionCount: number;
  /** When the document was last touched. Every document has one, so it is the
   *  line's last resort — and only that: it says nothing about WHICH document
   *  this is, so anything else outranks it. */
  updatedAt: string | null;
  capabilities: SurfaceCapabilities;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "48 min" / "1 hr 12 min". Rounds up, so a recording that happened never
 *  reads as one that did not. Exported because the recording row states the
 *  same length the summary does, and two implementations would drift. */
export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/**
 * The one-line summary under the title, describing what the reader is looking
 * at: the meeting first, then its people, then its length, falling back to the
 * document's own properties when the surface holds no meeting.
 *
 * Ordered most-identifying first because the line truncates from the right —
 * whichever part survives a narrow window should be the one that says which
 * meeting this is.
 *
 * Properties the surface cannot carry are ignored even when data is passed for
 * them: a text surface with an event in scope is a caller bug, and rendering
 * it would contradict the panel below, which omits the row entirely.
 *
 * Returns "" when there is nothing to say, leaving the resting state to the
 * caller rather than inventing a placeholder it would have to recognise.
 */
export function formatPropertySummary(input: PropertySummaryInput): string {
  const { capabilities } = input;
  const parts: string[] = [];

  if (capabilities.event && input.event) {
    parts.push(formatEventTime(input.event));
  }
  if (capabilities.attendees && input.attendeeCount > 0) {
    parts.push(plural(input.attendeeCount, 'person', 'people'));
  }
  if (capabilities.recording && input.durationSeconds !== null) {
    parts.push(formatDuration(input.durationSeconds));
  }
  if (input.tagCount > 0) {
    parts.push(plural(input.tagCount, 'tag', 'tags'));
  }
  if (input.collectionCount > 0) {
    parts.push(plural(input.collectionCount, 'collection', 'collections'));
  }

  // Only when nothing identifying was found: "Updated 2h ago" beats an empty
  // line on a plain note, and loses to anything that names the thing itself.
  if (parts.length === 0 && input.updatedAt) {
    return `Updated ${formatRelativeDate(input.updatedAt)}`;
  }

  return parts.slice(0, MAX_PARTS).join(' · ');
}
